// Utilidades para reconocer y combinar binarios de ESP32 en el navegador.
//
// Un ESP32 necesita cuatro piezas en la flash para arrancar:
//
//   0x1000   bootloader        \
//   0x8000   tabla de particiones > siempre las mismas (piezas base)
//   0xe000   selector de arranque/
//   0x10000  la aplicación      <- lo único que cambia en cada versión
//
// El Arduino IDE, al exportar, solo da la aplicación. Aquí se combina con las
// piezas base guardadas en la nube para obtener la "imagen completa" que se
// graba en el offset 0 y que funciona también en una placa recién comprada.
//
// Sin dependencias a propósito: así se puede probar con Node.

export const OFFSET_BOOTLOADER = 0x1000;
export const OFFSET_PARTICIONES = 0x8000;
export const OFFSET_BOOT_APP0 = 0xe000;
export const OFFSET_APP = 0x10000;

const MAGIC_IMAGEN = 0xe9; // cabecera de cualquier imagen de ESP32
const MAGIC_APP_DESC = 0xabcd5432; // descriptor presente solo en aplicaciones
const MAGIC_PARTICIONES = [0xaa, 0x50];
const RELLENO = 0xff;

const leerU32 = (bytes, off) =>
  (bytes[off] | (bytes[off + 1] << 8) | (bytes[off + 2] << 16) | (bytes[off + 3] << 24)) >>> 0;

const leerCadena = (bytes, off, largo) => {
  let fin = off;
  while (fin < off + largo && bytes[fin] !== 0) fin += 1;
  return new TextDecoder().decode(bytes.subarray(off, fin)).trim();
};

/**
 * Dice qué es un binario mirando su contenido, no su nombre.
 * Devuelve { tipo, etiqueta, ... } con tipo:
 *   'aplicacion' | 'imagen-completa' | 'bootloader' | 'particiones' | 'boot_app0' | 'desconocido'
 */
export function analizarBinario(bytes) {
  if (!bytes || bytes.length < 4) return { tipo: 'desconocido', etiqueta: 'archivo vacío o ilegible' };

  // Imagen completa: relleno hasta 0x1000, donde empieza el bootloader.
  if (bytes[0] === RELLENO && bytes.length > OFFSET_BOOTLOADER && bytes[OFFSET_BOOTLOADER] === MAGIC_IMAGEN) {
    const traeParticiones =
      bytes.length > OFFSET_PARTICIONES + 1 &&
      bytes[OFFSET_PARTICIONES] === MAGIC_PARTICIONES[0] &&
      bytes[OFFSET_PARTICIONES + 1] === MAGIC_PARTICIONES[1];
    const traeApp = bytes.length > OFFSET_APP && bytes[OFFSET_APP] === MAGIC_IMAGEN;
    return {
      tipo: 'imagen-completa',
      etiqueta: 'imagen completa',
      traeParticiones,
      traeApp,
      app: traeApp ? descripcionApp(bytes, OFFSET_APP) : null,
    };
  }

  if (bytes[0] === MAGIC_PARTICIONES[0] && bytes[1] === MAGIC_PARTICIONES[1]) {
    return { tipo: 'particiones', etiqueta: 'tabla de particiones' };
  }

  if (bytes[0] === MAGIC_IMAGEN) {
    const app = descripcionApp(bytes, 0);
    if (app) return { tipo: 'aplicacion', etiqueta: 'aplicación', app };
    return { tipo: 'bootloader', etiqueta: 'bootloader' };
  }

  // El selector de arranque no es una imagen: son dos entradas de datos OTA.
  if (bytes.length === 0x2000 && leerU32(bytes, 0) <= 1) {
    return { tipo: 'boot_app0', etiqueta: 'selector de arranque' };
  }

  return { tipo: 'desconocido', etiqueta: 'no se reconoce' };
}

/** Lee el descriptor de aplicación de ESP-IDF, si lo hay. */
function descripcionApp(bytes, base) {
  const off = base + 0x20;
  if (bytes.length < off + 0xb0) return null;
  if (leerU32(bytes, off) !== MAGIC_APP_DESC) return null;
  return {
    version: leerCadena(bytes, off + 0x10, 32),
    proyecto: leerCadena(bytes, off + 0x30, 32),
    hora: leerCadena(bytes, off + 0x50, 16),
    fecha: leerCadena(bytes, off + 0x60, 16),
    idf: leerCadena(bytes, off + 0x70, 32),
  };
}

/**
 * Une las piezas base con la aplicación y devuelve la imagen completa,
 * lista para grabarse en el offset 0. Equivale a `esptool merge_bin`.
 */
export function construirImagenCompleta({ bootloader, particiones, bootApp0, app }) {
  const piezas = [
    { nombre: 'bootloader', datos: bootloader, offset: OFFSET_BOOTLOADER, limite: OFFSET_PARTICIONES },
    { nombre: 'tabla de particiones', datos: particiones, offset: OFFSET_PARTICIONES, limite: OFFSET_BOOT_APP0 },
    { nombre: 'selector de arranque', datos: bootApp0, offset: OFFSET_BOOT_APP0, limite: OFFSET_APP },
    { nombre: 'aplicación', datos: app, offset: OFFSET_APP, limite: Infinity },
  ];

  for (const p of piezas) {
    if (!p.datos || p.datos.length === 0) throw new Error(`Falta la pieza: ${p.nombre}.`);
    if (p.offset + p.datos.length > p.limite) {
      throw new Error(`La pieza "${p.nombre}" no cabe en su hueco (ocupa ${p.datos.length} bytes).`);
    }
  }

  const total = OFFSET_APP + app.length;
  const imagen = new Uint8Array(total).fill(RELLENO);
  for (const p of piezas) imagen.set(p.datos, p.offset);
  return imagen;
}

/** Comprobación rápida de que una imagen recién construida tiene sus marcas. */
export function verificarImagenCompleta(imagen) {
  return (
    imagen[OFFSET_BOOTLOADER] === MAGIC_IMAGEN &&
    imagen[OFFSET_PARTICIONES] === MAGIC_PARTICIONES[0] &&
    imagen[OFFSET_PARTICIONES + 1] === MAGIC_PARTICIONES[1] &&
    imagen[OFFSET_APP] === MAGIC_IMAGEN
  );
}

/**
 * Tamaño de la partición de aplicación declarada en la tabla, para avisar si
 * el programa no cabe. La tabla son entradas de 32 bytes que empiezan por AA 50.
 */
export function tamanoParticionApp(particiones) {
  for (let off = 0; off + 32 <= particiones.length; off += 32) {
    if (particiones[off] !== MAGIC_PARTICIONES[0] || particiones[off + 1] !== MAGIC_PARTICIONES[1]) break;
    const tipo = particiones[off + 2];
    const subtipo = particiones[off + 3];
    if (tipo === 0 && subtipo === 0x10) return leerU32(particiones, off + 8); // app, ota_0
  }
  return null;
}
